/**
 * The client portal *site* API's vocabulary, reduced to the decisions the
 * property settings tab has to make.
 *
 * Sibling of `organizations/lib/portal-domain-errors.ts`, and deliberately the
 * same shape: pure, no React, no translations, no `@repo/core` runtime import
 * (the validation result types are type-only and erased at compile time). The
 * functions return *tokens*; `PropertyPortalSettings` turns a token into a
 * translated string. That keeps the branching testable without a DOM and stops
 * an unmapped server code from reaching an agency as a raw enum.
 */

import type {
	PortalPasswordValidation,
	PortalSlugValidation,
} from "@repo/core/client";

/** Leaf keys under `settings.property.clientPortal.errors`. */
export type PortalSiteErrorKey =
	| "invalidSlug"
	| "reservedSlug"
	| "slugTaken"
	| "noDomain"
	| "domainInactive"
	| "planRequired"
	| "invalidPassword"
	| "generic";

/**
 * Server `error` codes → copy.
 *
 * Two codes mean "your plan does not include this": `PLAN_REQUIRED` comes out
 * of the portal-site service, `SUBSCRIPTION_REQUIRED` out of the route's own
 * gate ahead of it. They are the same fact to an agency, so they share a line.
 *
 * `PASSWORD_UNREADABLE` is absent on purpose — it is not a toast but a state
 * the password row renders inline, so it is asked for by
 * {@link isPortalPasswordUnreadable} instead of mapped here. Anything else
 * absent is a code the UI has never been taught, which must read as a generic
 * failure rather than as "INTERNAL_ERROR" in a toast.
 */
const CODE_TO_ERROR_KEY: Record<string, PortalSiteErrorKey> = {
	INVALID_SLUG: "invalidSlug",
	RESERVED_SLUG: "reservedSlug",
	SLUG_TAKEN: "slugTaken",
	NO_DOMAIN: "noDomain",
	DOMAIN_INACTIVE: "domainInactive",
	INELIGIBLE_SUBJECT: "generic",
	PLAN_REQUIRED: "planRequired",
	SUBSCRIPTION_REQUIRED: "planRequired",
	INVALID_PASSWORD: "invalidPassword",
};

/** The API's code for a ciphertext that no longer decrypts. */
const PASSWORD_UNREADABLE = "PASSWORD_UNREADABLE";

/**
 * A non-2xx from the portal-site routes, carrying the machine-readable code
 * alongside the server's own prose. The prose is a fallback: we prefer our
 * translated copy, because the server's is English-only.
 */
export class PortalSiteRequestError extends Error {
	readonly code: string;
	readonly status: number;

	constructor(code: string, message: string, status: number) {
		super(message);
		this.name = "PortalSiteRequestError";
		this.code = code;
		this.status = status;
	}
}

/** Which translated error line to show for a failed request. */
export function portalSiteErrorKey(error: unknown): PortalSiteErrorKey {
	if (error instanceof PortalSiteRequestError) {
		return CODE_TO_ERROR_KEY[error.code] ?? "generic";
	}

	return "generic";
}

/**
 * Is this the "we can no longer decrypt the stored password" case?
 *
 * Distinct from every other failure because retrying cannot fix it — the
 * secret rotated, or the row came from the anonymised preview clone. The only
 * way out is issuing a new password, so the row says so instead of showing a
 * toast the agency would dismiss.
 */
export function isPortalPasswordUnreadable(error: unknown): boolean {
	return (
		error instanceof PortalSiteRequestError &&
		error.code === PASSWORD_UNREADABLE
	);
}

/**
 * The browser-side slug validator's rejection → the same copy the server would
 * have produced, so an inline field error and a server error read identically.
 */
export function portalSlugErrorKey(
	reason: Extract<PortalSlugValidation, { ok: false }>["reason"],
): PortalSiteErrorKey {
	return reason === "reserved" ? "reservedSlug" : "invalidSlug";
}

/**
 * The browser-side password validator's rejection. Both bounds land on the
 * same line, which names the range — telling someone their password is "too
 * long" without saying the limit only sends them back to guessing.
 */
export function portalPasswordErrorKey(
	_reason: Extract<PortalPasswordValidation, { ok: false }>["reason"],
): PortalSiteErrorKey {
	return "invalidPassword";
}

/**
 * The portal URL the agency is about to publish, built from parts that may not
 * exist yet.
 *
 * Returns null rather than a half-formed string when there is no hostname to
 * hang it on: showing "https://undefined/acme" as a preview is worse than
 * showing nothing. An empty slug renders as a placeholder segment so the shape
 * of the address is still visible while the field is being typed into.
 */
export function portalPreviewUrl(
	hostname: string | null | undefined,
	slug: string,
	emptySlugPlaceholder: string,
): string | null {
	if (!hostname) {
		return null;
	}

	return `https://${hostname}/${slug.trim() || emptySlugPlaceholder}`;
}
